import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';

import { DirectoryCard, Map, Typography, EmptyComponent } from '../../../components';
import * as S from './DirectoryCardsGrid.styles';
import { formatNumberWithCommas } from '../../../utils/content-formatter';

const VIEW_OPTIONS = {
	grid: 'grid',
	list: 'list',
};

const DirectoryCardsGrid = ({ data, role, handleSaveUnsave, totalItems }) => {
	const [showMap, setShowMap] = useState(false);
	const [selectedViewOption, setSelectedViewOption] = useState(VIEW_OPTIONS.grid);
	const [backLink] = useState(() => {
		switch (role) {
			case 'teacher':
				return '/teacher/requests';
			case 'business':
				return '/business/requests';
			case 'admin':
			case 'super':
				return '/admin/requests';
			default:
				return '/directory';
		}
	});

	function generateLinkToResource(request) {
		switch (role) {
			case 'teacher':
				return `/teacher/requests/${request.id}`;
			case 'business':
				return `/business/requests/${request.id}`;
			case 'admin':
			case 'super':
				return `/admin/requests/${request.id}`;
			default:
				return `/directory/${request.id}`;
		}
	}

	return (
		<S.DirectoryCardsGrid showMap={showMap}>
			<div>
				<S.ResultsAndActionsWrapper>
					<Typography tag="p" variation="2">
						{totalItems === 1 ? '1 Results' : `${formatNumberWithCommas(totalItems)} Results`}
					</Typography>
					<div className="actions-wrapper">
						<S.ToggleButton className="grid-button first" active={selectedViewOption === 'grid'} onClick={() => setSelectedViewOption('grid')}>
							<FontAwesomeIcon icon={['fal', 'th-large']} />
						</S.ToggleButton>
						<S.ToggleButton className="grid-button last" active={selectedViewOption === 'list'} onClick={() => setSelectedViewOption('list')}>
							<FontAwesomeIcon icon={['fal', 'list']} />
						</S.ToggleButton>
						<S.ToggleButton active={showMap} onClick={() => setShowMap((prev) => !prev)}>
							<FontAwesomeIcon icon={['fal', 'map']} />
							<Typography tag="p" variation="2">
								Show Map
							</Typography>
						</S.ToggleButton>
					</div>
				</S.ResultsAndActionsWrapper>
				{data.length > 0 ? (
					<S.CardsWrapper viewOption={showMap && selectedViewOption === 'grid' ? 'list' : selectedViewOption}>
						{data.map((request) => (
							<DirectoryCard
								key={request.id}
								to={generateLinkToResource(request)}
								back={backLink}
								variation={showMap && selectedViewOption === 'grid' ? 'list-alt' : selectedViewOption}
								thumbnail={request.coverImageObj['800']}
								bookmarked={request.isSaved}
								role={role}
								status={request.status}
								type={request.category}
								title={request.title}
								location={request?.creator?.business?.name || request?.business?.name || `${request?.creator?.firstName} ${request?.creator?.lastName}`}
								bookmarkAction={() => {
									handleSaveUnsave(request.id);
								}}
								isArchived={request.isArchived}
							/>
						))}
					</S.CardsWrapper>
				) : (
					<EmptyComponent title="No available requests" message="Check back later to view available requests in the directory." icon={['fal', 'books']} />
				)}
			</div>
			{showMap && (
				<S.MapWrapper>
					<Map className="map-component" role={role} enableGeolocationFallback={!role} markers={data.filter((datum) => datum.type !== 'information')} entityType="request" />
				</S.MapWrapper>
			)}
		</S.DirectoryCardsGrid>
	);
};

DirectoryCardsGrid.propTypes = {
	data: PropTypes.array,
	role: PropTypes.string,
	handleSaveUnsave: PropTypes.func,
	totalItems: PropTypes.number,
};

export { DirectoryCardsGrid };
